import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  List,
  ListItem,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/system';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';
import {
  getCommunicationPreferences,
  isAccountDetailsLoading,
  updateCommunicationPreferences,
} from '../../redux/userSlice';
import {
  COMMUNICATION_PREFERENCES_SECTION,
  SALES_CONDITIONS,
} from '../../constants/strings';
import { ACCOUNT_DETAILS_FIELDS } from '../../constants/forms';
import { useAuth } from '../../features/Auth';
import { useCustomSnackbar } from '../../features/CustomSnackbar';
import ProhibitedModal from '../ProhibitedModal';
import {
  PRIVACY_POLICY,
  PROHIBITED_ITEMS_URL,
} from '../../constants/externalLinks';
import DutyAndTaxInformation from './components/DutyAndTaxInformation';

const StyledTypography = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: 'normal',
  letterSpacing: '0.4px',
}));

const StyledBox = styled(Typography)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const SalesConditions = ({
  onAcceptTerms,
  salesConditionsLink = '#',
  deliveryCountryName,
  isAvailableProhibitedCondition,
  showProhibitedItemsBullet = true,
  showDutyAndTaxInformation = true,
  showOrderPlacementBulletList = true,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { showError, showDefault } = useCustomSnackbar();

  const { currentSession } = useAuth();
  const notifyByEmailUpdateLoading = useSelector(isAccountDetailsLoading);
  const { notifyByEmail } = useSelector(getCommunicationPreferences);

  const onChangeNotifyByEmail = useCallback(async ({ target }) => {
    if (target) {
      try {
        await dispatch(
          updateCommunicationPreferences({ [target.name]: target.checked })
        ).unwrap();

        showDefault({
          message: COMMUNICATION_PREFERENCES_SECTION.SNACKBAR_MESSAGES.SUCCESS,
        });
      } catch (e) {
        showError({
          message: COMMUNICATION_PREFERENCES_SECTION.SNACKBAR_MESSAGES.FAILED,
        });
      }
    }
  }, []);

  const [checkedTerms, setCheckedTerms] = useState(false);
  const [checkedProhibitedItem, setCheckedProhibitedItem] = useState(false);
  const [isOpenProhibitedModal, setIsOpenProhibitedModal] = useState(false);

  useEffect(() => {
    onAcceptTerms(
      (checkedProhibitedItem || !isAvailableProhibitedCondition) && checkedTerms
    );
  }, [
    checkedTerms,
    checkedProhibitedItem,
    isAvailableProhibitedCondition,
    onAcceptTerms,
  ]);

  return (
    <>
      <Box>
        <Paper sx={{ p: 2, my: 2 }}>
          <Typography variant='h3'>{SALES_CONDITIONS.TITLE}</Typography>
          <StyledBox sx={{ mt: 2 }}>
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Checkbox
                  id='terms-and-conditions-checkbox'
                  checked={checkedTerms}
                  onChange={event => setCheckedTerms(event.target.checked)}
                  sx={{ color: 'primary.main' }}
                />
              }
              label={
                <StyledTypography variant='body1'>
                  {SALES_CONDITIONS.ACCEPT_THE}
                  <Link
                    href={salesConditionsLink}
                    sx={{
                      textDecoration: 'none',
                    }}
                    target='_blank'
                    rel='nofollow noopener noreferrer'
                  >
                    {SALES_CONDITIONS.TERMS_AND_CONDITIONS}
                  </Link>
                </StyledTypography>
              }
            />
          </StyledBox>
          {isAvailableProhibitedCondition && (
            <StyledBox>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    id='prohibited-items-checkbox'
                    checked={checkedProhibitedItem}
                    onChange={event =>
                      setCheckedProhibitedItem(event.target.checked)
                    }
                    sx={{ color: 'primary.main' }}
                  />
                }
                label={
                  <StyledTypography variant='body1'>
                    {SALES_CONDITIONS.CONFIRM_THAT_MY_SHIPMENT}
                    <Button
                      variant='link'
                      onClick={() => setIsOpenProhibitedModal(true)}
                      sx={{
                        cursor: 'pointer',
                        color: theme.vars.palette.primary.main,
                        textTransform: 'none',
                        padding: 0,
                        minWidth: 0,
                        fontSize: '0.8rem',
                        fontWeight: 400,
                        display: 'inline',
                      }}
                    >
                      {SALES_CONDITIONS.PROHIBITED_ITEMS}
                    </Button>
                  </StyledTypography>
                }
              />
            </StyledBox>
          )}
          {currentSession?.uid && (
            <StyledBox>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    id='notify-by-email-checkbox'
                    name={
                      ACCOUNT_DETAILS_FIELDS.COMMUNICATION_PREFERENCES.EMAIL
                        .NAME
                    }
                    disabled={notifyByEmailUpdateLoading}
                    onChange={onChangeNotifyByEmail}
                    defaultChecked={notifyByEmail}
                    sx={{ color: 'primary.main' }}
                  />
                }
                label={
                  <StyledTypography variant='body1'>
                    {SALES_CONDITIONS.IF_YOU_LIKE_TO_RECEIVE}
                  </StyledTypography>
                }
              />
            </StyledBox>
          )}
          {showOrderPlacementBulletList ? (
            <Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant='body1'>
                  {SALES_CONDITIONS.DUTY_AND_TAX_INFO.BY_PLACING_THIS_ORDER}
                </Typography>
                <List>
                  <ListItem
                    sx={{
                      display: 'list-item',
                      listStyleType: 'disc',
                      ml: 4,
                      pr: 4,
                    }}
                  >
                    <Typography variant={'body1'}>
                      {SALES_CONDITIONS.DUTY_AND_TAX_INFO.YOU_HAVE_READ}
                      <Link
                        href={salesConditionsLink}
                        underline='none'
                        target='_blank'
                        sx={{ display: 'inline' }}
                      >
                        {
                          SALES_CONDITIONS.DUTY_AND_TAX_INFO
                            .TERMS_AND_CONDITIONS
                        }
                      </Link>
                      {SALES_CONDITIONS.DUTY_AND_TAX_INFO.WE_RESERVE}
                    </Typography>
                  </ListItem>
                  {showProhibitedItemsBullet ? (
                    <ListItem
                      sx={{
                        display: 'list-item',
                        listStyleType: 'disc',
                        ml: 4,
                        pr: 4,
                      }}
                    >
                      <Typography variant={'body1'}>
                        {SALES_CONDITIONS.DUTY_AND_TAX_INFO.YOU_ARE_NOT_SENDING}
                        <Link
                          href={PROHIBITED_ITEMS_URL}
                          underline='none'
                          target='_blank'
                          sx={{ display: 'inline' }}
                        >
                          {SALES_CONDITIONS.DUTY_AND_TAX_INFO.PROHIBITED_ITEMS}
                        </Link>
                        {SALES_CONDITIONS.DUTY_AND_TAX_INFO.LIST_SENDING}
                      </Typography>
                    </ListItem>
                  ) : null}
                </List>
                <Typography variant={'body1'}>
                  {SALES_CONDITIONS.DUTY_AND_TAX_INFO.VIEW_OUR}
                  <Link
                    href={PRIVACY_POLICY}
                    underline='none'
                    target='_blank'
                    sx={{ display: 'inline' }}
                  >
                    {SALES_CONDITIONS.DUTY_AND_TAX_INFO.PRIVACY_POLICY}
                  </Link>
                  {SALES_CONDITIONS.DUTY_AND_TAX_INFO.TO_UNDERSTAND}
                </Typography>
              </Box>
            </Box>
          ) : null}
          {showDutyAndTaxInformation ? (
            <DutyAndTaxInformation destinationCountry={deliveryCountryName} />
          ) : null}
        </Paper>
      </Box>
      {isAvailableProhibitedCondition ? (
        <ProhibitedModal
          isOpen={isOpenProhibitedModal}
          onClose={() => setIsOpenProhibitedModal(false)}
          termsAndConditionsLink={salesConditionsLink}
        />
      ) : null}
    </>
  );
};

SalesConditions.propTypes = {
  onAcceptTerms: PropTypes.func.isRequired,
  salesConditionsLink: PropTypes.string.isRequired,
  isAvailableProhibitedCondition: PropTypes.bool,
  showProhibitedItemsBullet: PropTypes.bool,
  showDutyAndTaxInformation: PropTypes.bool,
  showOrderPlacementBulletList: PropTypes.bool,
};

export default SalesConditions;
